import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  WHATS_NEW_ENABLED,
  aggregateNews,
  formatDate,
  latestNewsId,
  loadNews,
  markNewsSeen,
  proteinArrivalEntry,
  unseenNews,
} from './news';

/**
 * WhatsNew — the three surfaces over the news feed (`GET /news`):
 *
 *   <WhatsNewBanner />  a dismissible strip above the grid (passive)
 *   <WhatsNewModal />   shown once, and only for entries marked `major`
 *   <NewsList />        the full aggregated history, for the About page
 *
 * All three read one shared fetch. Repeat entries are aggregated by
 * `aggregateNews()`, so "Small Airway Atlas server upgrades" appears once with
 * a date interval and an ×N badge rather than four times.
 *
 * Two kinds of entry flow through the same renderer: the hand-written ones an
 * admin created in the news central, and one automatic entry for the proteins
 * that arrived since this user's previous visit. The automatic one carries no
 * id, because it is per-user and there is nothing durable to acknowledge.
 */

const KIND_LABELS = {
  feature: 'New',
  data: 'Data',
  fix: 'Fix',
  maintenance: 'Maintenance',
};

/** Dismissing the strip is a per-browser preference, so it stays in localStorage. */
const BANNER_KEY = 'saa.whatsnew.bannerDismissed';

// Mirror of the acknowledged id. The server copy (userprofile.news_seen_id) is
// the one that travels between devices; this one covers the cases where the
// server has nowhere to put it -- a session whose e-mail has no profile row --
// so the same news does not reappear on every load.
const SEEN_KEY = 'saa.whatsnew.seen';

const readKey = key => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeKey = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode — degrade to always-shown */ }
};

/**
 * Loads the feed and works out what this user has not been shown.
 *
 * "Seen" is stored server-side (`userprofile.news_seen_id`) so the same news
 * does not reappear on a second machine. A first-ever visit acknowledges
 * everything silently: someone signing in for the first time should not be met
 * by the full history.
 */
export function useNews() {
  const [data, setData] = useState(null);
  const [ack, setAck] = useState(null);         // locally acknowledged id
  const [dismissed, setDismissed] = useState(() => readKey(BANNER_KEY));

  useEffect(() => {
    if (!WHATS_NEW_ENABLED) return undefined;
    let live = true;
    loadNews().then(payload => {
      if (!live) return;
      setData(payload);
      // A first-ever visit acknowledges everything silently -- but only when
      // neither the server nor this browser has a marker, otherwise a profile
      // row that cannot store one would re-acknowledge on every load.
      if (payload.seen_id == null && !readKey(SEEN_KEY)) {
        const latest = latestNewsId(payload.events);
        if (latest) {
          markNewsSeen(latest);
          writeKey(SEEN_KEY, String(latest));
          setAck(latest);
        }
      }
    });
    return () => { live = false; };
  }, []);

  const events = data?.events || [];
  const latestId = useMemo(() => latestNewsId(events), [events]);
  const localSeen = Number(readKey(SEEN_KEY)) || 0;
  // Whichever marker is further along wins: the server's, this browser's, or
  // one set during this render pass.
  const seenId = Math.max(ack || 0, data?.seen_id || 0, localSeen) || null;

  const all = useMemo(() => aggregateNews(events), [events]);
  const arrivals = useMemo(() => proteinArrivalEntry(data?.new_proteins), [data]);

  const unseen = useMemo(() => {
    if (!data) return [];
    const fresh = unseenNews(seenId, events);
    return arrivals ? [arrivals, ...fresh] : fresh;
  }, [data, events, seenId, arrivals]);

  const markAllSeen = useCallback(() => {
    if (latestId) {
      markNewsSeen(latestId);
      writeKey(SEEN_KEY, String(latestId));
    }
    setAck(latestId);
    writeKey(BANNER_KEY, String(latestId || ''));
    setDismissed(String(latestId || ''));
  }, [latestId]);

  const dismissBanner = useCallback(() => {
    writeKey(BANNER_KEY, String(latestId || ''));
    setDismissed(String(latestId || ''));
  }, [latestId]);

  // The modal interrupts, so it is reserved for entries flagged `major`.
  const showModal = unseen.some(entry => entry.major);
  const showBanner =
    !showModal && unseen.length > 0 && dismissed !== String(latestId || '');

  return { all, unseen, showModal, showBanner, markAllSeen, dismissBanner, loading: !data };
}

function KindBadge({ kind }) {
  const label = KIND_LABELS[kind] || KIND_LABELS.feature;
  return <span className={`whatsnew-kind whatsnew-kind-${kind || 'feature'}`}>{label}</span>;
}

/** '×4', expandable into the exact dates behind an aggregated entry. */
function OccurrenceBadge({ entry }) {
  const [open, setOpen] = useState(false);
  if (entry.count < 2) return null;
  const dateList = entry.dates.map(formatDate).join(', ');
  return (
    <>
      <button
        type="button"
        className="whatsnew-count"
        onClick={() => setOpen(o => !o)}
        title={`${entry.count} occasions: ${dateList}`}
        aria-expanded={open}
      >
        ×{entry.count}
      </button>
      {open && (
        <ul className="whatsnew-dates">
          {entry.dates.map(d => <li key={d}>{formatDate(d)}</li>)}
        </ul>
      )}
    </>
  );
}

/** The proteins behind an arrival entry, as links to their pages. */
function ProteinLinks({ proteins, overflow }) {
  if (!proteins || !proteins.length) return null;
  return (
    <ul className="whatsnew-proteins" role="list">
      {proteins.map(p => (
        <li key={p.uniprot_id}>
          <Link to={`/protein/${encodeURIComponent(p.uniprot_id)}/`}>
            {p.gene || p.uniprot_id}
          </Link>
        </li>
      ))}
      {overflow > 0 && <li className="whatsnew-proteins-more">and {overflow} more</li>}
    </ul>
  );
}

/** One aggregated entry. */
export function NewsEntryItem({ entry }) {
  return (
    <li className="whatsnew-entry">
      <div className="whatsnew-entry-head">
        <KindBadge kind={entry.kind} />
        <span className="whatsnew-entry-title">{entry.title}</span>
        {entry.dateLabel && <span className="whatsnew-entry-date">{entry.dateLabel}</span>}
        <OccurrenceBadge entry={entry} />
      </div>
      {entry.detail && <p className="whatsnew-entry-detail">{entry.detail}</p>}
      {entry.items.length > 0 && (
        <ul className="whatsnew-entry-items">
          {entry.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )}
      {entry.isProteinArrival && (
        <ProteinLinks proteins={entry.proteins} overflow={entry.overflow} />
      )}
    </li>
  );
}

/** Full history, aggregated — used on the About page. */
export function NewsList({ entries }) {
  const { all, loading } = useNews();
  if (!WHATS_NEW_ENABLED) return null;
  const list = entries || all;
  if (loading && !entries) return <p className="whatsnew-empty">Loading…</p>;
  if (!list.length) return <p className="whatsnew-empty">Nothing has been published yet.</p>;
  return (
    <ul className="whatsnew-list" role="list">
      {list.map(entry => <NewsEntryItem key={entry.key} entry={entry} />)}
    </ul>
  );
}

/**
 * Slim strip above the grid. The close button stays visible (dimmed until
 * hover) — a hover-only control is invisible on touch and easy to miss.
 * Dismissal is remembered per release, so it comes back on the next change.
 */
export function WhatsNewBanner() {
  const navigate = useNavigate();
  const { unseen, showBanner, dismissBanner, markAllSeen } = useNews();
  if (!WHATS_NEW_ENABLED || !showBanner) return null;

  const [first, ...rest] = unseen;
  const openFullList = () => {
    markAllSeen();
    navigate('/about#whats-new');
  };

  return (
    <div className="whatsnew-banner" role="status">
      <span className="material-symbols-outlined whatsnew-banner-icon" aria-hidden="true">
        campaign
      </span>
      <span className="whatsnew-banner-text">
        <strong>What&rsquo;s new:</strong> {first.title}
        {rest.length > 0 && ` — and ${rest.length} more change${rest.length > 1 ? 's' : ''}`}
      </span>
      <button type="button" className="whatsnew-banner-link" onClick={openFullList}>
        See all changes
      </button>
      <button
        type="button"
        className="whatsnew-banner-close"
        onClick={dismissBanner}
        aria-label="Hide this notice"
        title="Hide this notice until the next change"
      >
        <span className="material-symbols-outlined" aria-hidden="true">close</span>
      </button>
    </div>
  );
}

/** Shown at most once per release, and only when something `major` landed. */
export function WhatsNewModal() {
  const navigate = useNavigate();
  const { unseen, showModal, markAllSeen } = useNews();
  const [closed, setClosed] = useState(false);

  const close = useCallback(() => {
    setClosed(true);
    markAllSeen();
  }, [markAllSeen]);

  useEffect(() => {
    if (!showModal || closed) return undefined;
    const onKey = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, closed, close]);

  if (!WHATS_NEW_ENABLED || !showModal || closed) return null;

  return (
    <div className="whatsnew-overlay" onClick={close}>
      <div
        className="whatsnew-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsnew-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <h2 id="whatsnew-modal-title" className="whatsnew-modal-title">
          What&rsquo;s new in the Small Airway Atlas
        </h2>
        <NewsList entries={unseen} />
        <div className="whatsnew-modal-actions">
          <button
            type="button"
            className="pill-btn"
            onClick={() => { close(); navigate('/about#whats-new'); }}
          >
            See all changes
          </button>
          <button type="button" className="pill-btn pill-btn-primary" onClick={close}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
